import type { FilterNode } from '@ofmos/shared/filter-ast';
import type { AggregateRequest, GroupDimension } from '@ofmos/shared/record-group';
import type { OrderBy } from '@ofmos/shared/record-query';
import type { DropTarget, MetadataVisibility, MyTaskCountFilter, MyTasksFilter } from './client';

/**
 * INV-13 FRONTEND CACHE DISCIPLINE. Every TanStack Query key in the app is built
 * here, so "is this the same cache entry?" is answered by reading one file.
 *
 * The canonical entry for a record is `['record', objectId, recordId]`. LIST
 * queries never hold record objects: they hold the ids in order, and each row
 * resolves its data from the canonical key. One mutation therefore re-renders one
 * row, and a record cannot exist in two shapes in the cache.
 */
export interface RecordListSignature {
  readonly select: readonly string[];
  /**
   * Part of the KEY, not a parameter next to it: two filters are two different lists, and
   * sharing one cache entry between them is how a filtered view shows the unfiltered page
   * for a moment. It is also what lets the optimistic path find the filtered lists
   * (`optimisticList.ts`) after a write.
   */
  readonly filter?: FilterNode | undefined;
  readonly orderBy?: readonly OrderBy[] | undefined;
  readonly limit?: number | undefined;
  /**
   * THE ARCHIVED SEGMENT (admin-area.md D21), in the key for the reason `filter` is.
   *
   * Active and Archived are two different lists over the same object with the same select,
   * sort and limit - so without this they are ONE cache entry, and switching the segment
   * would paint the live rows under the Archived heading until the refetch landed. The
   * condition that actually narrows the query is built inside the client implementation
   * (`RecordQueryOptions`), so it is invisible to `filter` and has to be keyed on here.
   */
  readonly archivedOnly?: boolean | undefined;
  /**
   * D20, and it is in the KEY for the reason `archivedOnly` is: two callers of the
   * same filter, one of which asked for the total, produce two different cache
   * ENTRIES - the counted one and the uncounted one - and sharing an entry between
   * them means whichever query ran first decides whether the other ever sees a count.
   * `optimisticList.ts` reads the signature back off the key, so a field that reaches
   * the server without passing through here is a signature that lies.
   */
  readonly withTotalCount?: boolean | undefined;
}

/**
 * THE GROUPED READ'S SIGNATURE (group-by-aggregation.md D25).
 *
 * `RecordListSignature` is the model and its own comment is the rule: "a field that
 * reaches the server without passing through here is a signature that lies". So this
 * carries the EMBEDDED query's signature plus exactly the members of `RecordGroupQuery`
 * (D1) and no others.
 *
 * Two absences are decisions rather than omissions. There is no `groupOrderBy`, because
 * there is no such wire field - group order is DERIVED from the dimension (D11, D17). And
 * there is no `hideEmptyGroups`, because that toggle never reaches the server at all: it
 * is a client display setting that happens to persist on the view (D29), so toggling it
 * changes no cache key and issues no request.
 */
export interface RecordGroupSignature {
  readonly query: RecordListSignature;
  readonly groupBy: readonly GroupDimension[];
  readonly aggregates?: readonly AggregateRequest[] | undefined;
  readonly groupLimit?: number | undefined;
  readonly rowsPerGroup?: number | undefined;
}

export const queryKeys = {
  health: () => ['health'] as const,
  /** The signed-in user, or null. Written by the login/logout mutations. */
  session: () => ['session'] as const,
  /** Accounts, for the admin screen that hands out access. */
  users: () => ['users'] as const,
  /**
   * THE ADMIN AREA (admin-area.md).
   *
   * `apiKeys` is its own entry rather than part of `users`: a key is a credential, not a
   * person, and the two lists are refetched by different mutations.
   *
   * `agentPolicy` is ONE key for the whole snapshot - both global switches and every
   * per-object row - because the server computes `effectiveDirectWrite` from the switch
   * and splitting them into two cache entries is how a screen ends up rendering "direct"
   * beside a brake that is on.
   */
  apiKeys: () => ['apiKeys'] as const,
  /**
   * PENDING INVITATIONS (accounts F1). Its own entry beside `users` rather than part of
   * it, for the reason `apiKeys` is its own: an invitation is not an account, it is an
   * account that does not exist yet, and folding the two would make "who is in this
   * instance" a list with rows nobody can sign in as.
   */
  invitations: () => ['invitations'] as const,
  /**
   * ACCESS ROLES (roles-and-permissions.md section 6). Its own entry rather than part of
   * `users`, for the reason `apiKeys` is its own: a role is a definition, a user is a
   * holder, and assigning one has to invalidate both without refetching the other's list
   * every time somebody renames a role.
   */
  roles: () => ['roles'] as const,
  role: (roleId: string) => ['roles', roleId] as const,
  roleHolders: (roleId: string) => ['roles', roleId, 'holders'] as const,
  roleAdministrators: () => ['roles', 'administrators'] as const,
  agentPolicy: () => ['agentPolicy'] as const,
  instance: () => ['instance'] as const,
  /**
   * login.md 2.3: the installation's name, read once by the sign-in screen with no session.
   * Its own key rather than part of `instance`: that one is the admin panel's admin-only
   * read, and a shared entry would let a signed-out screen's answer stand in for it.
   */
  workspace: () => ['workspace'] as const,
  /** Open phase-B actions. Polled while the Background changes card is on screen. */
  migrationActions: () => ['migrationActions'] as const,
  /** `/ready`: database up/down and the webhook outbox depth. */
  ready: () => ['ready'] as const,

  /**
   * THE MODEL, PER VISIBILITY (admin-area.md D8).
   *
   * The mode is the LAST segment on purpose. Every invalidation in the app - the live
   * stream's `metadataInvalidations`, the metadata mutations, the resync after a dropped
   * stream - keys on the prefix (`['fields', objectId]`), and TanStack matches by prefix,
   * so both visibilities are refreshed by the same notice and cannot drift apart.
   *
   * Two entries rather than one filtered client-side: the admin list holds rows no other
   * screen may ever see, and one cache entry serving both is precisely how a junction
   * object ends up in the sidebar.
   */
  objects: (visibility: MetadataVisibility = 'visible') => ['objects', visibility] as const,
  object: (objectId: string, visibility: MetadataVisibility = 'visible') =>
    ['object', objectId, visibility] as const,
  fields: (objectId: string, visibility: MetadataVisibility = 'visible') =>
    ['fields', objectId, visibility] as const,
  /**
   * D2: which related sections a record page shows. Keyed by the object rather than by
   * the record, because it is a question about the METADATA GRAPH - every agency page
   * in the app shares this one entry.
   */
  inboundRelations: (objectId: string) => ['inboundRelations', objectId] as const,
  /**
   * D27: what may anchor a related filter on this object. ONE key, one read, two
   * consumers named in the same module (INV-13) - the filter editor's anchor select and
   * `pruneFilter`'s anchor validation. Keyed by the object for the reason above: it is a
   * question about the metadata graph, not about a record or a view.
   */
  filterAnchors: (objectId: string) => ['filterAnchors', objectId] as const,
  /** D5: the dry run behind one drop dialog. Never cached across dialogs. */
  dropPreview: (target: DropTarget) =>
    [
      'dropPreview',
      target.kind,
      target.kind === 'object' ? target.objectId : target.fieldId,
    ] as const,

  /** CANONICAL record key. Mutations write here optimistically. */
  record: (objectId: string, recordId: string) => ['record', objectId, recordId] as const,
  /** Holds `{ ids, nextCursor }` only. */
  recordList: (objectId: string, signature: RecordListSignature) =>
    ['recordList', objectId, signature] as const,
  /**
   * THE PAGED FORM of the same list (D20), and a DIFFERENT key on purpose.
   *
   * `useInfiniteQuery` stores `{pages, pageParams}` where `useQuery` stores one page,
   * so one key serving both would hand whichever hook mounted second a shape it cannot
   * read. The prefix is still `recordList`, so every invalidation in the app
   * (`['recordList', objectId]`) reaches both without knowing either exists.
   */
  recordListPages: (objectId: string, signature: RecordListSignature) =>
    ['recordList', objectId, 'pages', signature] as const,
  /**
   * THE GROUPED READ, UNDER ITS OWN PREFIX (D25), and the separation is the one place a
   * shortcut here would cost a real bug.
   *
   * `reconcileFilteredLists` walks every entry under `['recordList', objectId]`, reads the
   * signature off slot 2 or slot 3 and knows exactly TWO data shapes. A grouped payload is
   * a third. Under the same prefix it would fall through that function's
   * `filter === undefined` guard and be silently never reconciled - which is not
   * hypothetical: `optimisticList.ts` records that exact bug having already shipped once,
   * for the paged shape. A separate prefix makes the grouped entry invisible to a function
   * that cannot handle it.
   *
   * The price is that prefix matching stops reaching it for free, and that price is paid
   * in ONE place: `recordReadKeys` in `live/invalidation.ts` names both prefixes, every
   * write path calls it, and `reconcileFilteredLists` invalidates this one itself.
   */
  recordGroups: (objectId: string, signature: RecordGroupSignature) =>
    ['recordGroups', objectId, signature] as const,
  recordEvents: (objectId: string, recordId: string) =>
    ['recordEvents', objectId, recordId] as const,

  search: (term: string) => ['search', term] as const,

  /** INV-10 approval queue. The count is its own key: it is rendered on every screen. */
  pendingChanges: (status?: string) => ['pendingChanges', status ?? 'pending'] as const,
  pendingCount: () => ['pendingCount'] as const,
  pendingChange: (id: string) => ['pendingChange', id] as const,

  /**
   * IDENTITY RESOLUTION. The namespace list is its own key rather than part of the
   * metadata graph: a namespace is not a field, it is what decides whether two fields
   * are comparable, and every identifier picker in the product reads the same list.
   */
  identityNamespaces: () => ['identityNamespaces'] as const,
  /** The detector PAUSE toggle (section 3). One switch, one cache entry. */
  identityDetector: () => ['identityDetector'] as const,
  /** A record's own keys plus the cross-object hits on them (2.6). */
  identityPanel: (objectId: string, recordId: string) =>
    ['identityPanel', objectId, recordId] as const,
  recordDuplicates: (objectId: string, recordId: string) =>
    ['recordDuplicates', objectId, recordId] as const,
  objectDuplicates: (objectId: string) => ['objectDuplicates', objectId] as const,
  /**
   * Keyed by the PAIR, not by the proposal: the same preflight answers a queued
   * proposal and a merge somebody is about to start from the duplicates list, and two
   * cache entries for one server answer is how the two screens start disagreeing.
   */
  mergePreflight: (objectId: string, winnerRecordId: string, loserRecordId: string) =>
    ['mergePreflight', objectId, winnerRecordId, loserRecordId] as const,
  merges: (scope: { readonly objectId?: string; readonly recordId?: string }) =>
    ['merges', scope.objectId ?? '', scope.recordId ?? ''] as const,
  unmergePreflight: (mergeId: string) => ['unmergePreflight', mergeId] as const,
  /**
   * NOTES, TASKS AND COMMENTS (notes-tasks-comments.md 3.5).
   *
   * Keyed `[kind, objectId, recordId]` in that order for one reason: the SSE notice carries
   * OBJECT ids and nothing finer, so `['notes', objectId]` has to be a usable prefix. Put
   * the record first and the live layer could only ever invalidate every note in the app.
   *
   * Comments are ONE key per record with note threads included, not one per note: grouping
   * by `noteId` is a partition of data already in hand, and a key per note card would turn
   * a scroll into a request storm.
   */
  notes: (objectId: string, recordId: string) => ['notes', objectId, recordId] as const,
  tasks: (objectId: string, recordId: string) => ['tasks', objectId, recordId] as const,
  comments: (objectId: string, recordId: string) => ['comments', objectId, recordId] as const,
  /**
   * The global feed (12.2). Keyed by the WHOLE filter, like `recordList`: Mine and Everyone
   * are two different lists, and one entry serving both is how the team view flashes your
   * own tasks before it corrects itself.
   */
  myTasks: (filter: MyTasksFilter) => ['myTasks', filter] as const,
  /**
   * HOW MANY OPEN TASKS - and it is PARAMETERISED, which is not a refinement but a fix
   * for an INV-13 hazard the dashboard would otherwise have created (dashboard.md D11).
   *
   * Three callers want this number with three meanings: `TaskBadge` wants every open task
   * assigned to you, on every screen; the dashboard tile wants it narrowed to today; and
   * the same tile under Everyone wants a third set. One key serving them is a cache
   * collision, and THE LOSER IS THE BADGE - it would silently report only what is due
   * today and everybody's real backlog would vanish from the navigation, which is a data
   * lie in the one place the product promises that being assigned a task is not something
   * you learn by navigating somewhere.
   *
   * Keyed on the WHOLE filter object rather than on a positional `dueTo`, matching
   * `myTasks` above: a third parameter later adds no third key shape. `undefined` keeps
   * `['myTaskCount', null]` for the badge, and the prefix invalidation in
   * `live/invalidation.ts` still reaches every entry under it, so no SSE change is needed.
   */
  myTaskCount: (filter?: MyTaskCountFilter) => ['myTaskCount', filter ?? null] as const,
  /** The assignee picker's user list, shared by every task surface in the product. */
  annotationUsers: () => ['annotationUsers'] as const,

  /**
   * SAVED VIEWS (D14): per OBJECT, because that is the only scope a view has - there is
   * no user in the key, no workspace and no team (INV-15). The switcher, the URL and the
   * default resolution all read this one entry.
   */
  /**
   * `reports.md` T7. The RUN is keyed separately from the definition, and by the range and
   * zone as well: two ranges are two different answers to one question, and caching them
   * under one key would show last quarter's numbers under this quarter's label for as long
   * as the cache lived.
   */
  reports: () => ['reports'] as const,
  report: (reportId: string) => ['reports', reportId] as const,
  reportRun: (reportId: string, range: string, timeZone: string) =>
    ['reports', reportId, 'run', range, timeZone] as const,
  views: (objectId: string) => ['views', objectId] as const,

  /**
   * THE CALLER'S OWN PREFERENCES (dashboard.md D33). ONE entry for the whole bag, and it
   * is invalidated by nothing but its own mutation: no other user can change it, so no
   * SSE notice needs to carry it and a record notice must not throw it away.
   */
  preferences: () => ['preferences'] as const,

  /** Outbound webhooks. The delivery log is keyed by its filter: two filters, two lists. */
  webhooks: () => ['webhooks'] as const,
  webhook: (id: string) => ['webhook', id] as const,
  webhookDeliveries: (id: string, status?: string) =>
    ['webhookDeliveries', id, status ?? 'all'] as const,

  /**
   * WORKFLOWS (workflow-builder.md D45). One entry for the list, one per workflow, and the
   * run list keyed by its SEGMENT - All, Failed and Stopped are three different answers to
   * three different questions, and sharing one cache entry between them is how the Failed
   * tab paints the full list for a frame.
   *
   * `workflowSettings` is its own entry rather than part of the list, for the reason
   * `agentPolicy` is: the brake is rendered above the list and read by a screen that may
   * not have the list at all.
   */
  workflows: () => ['workflows'] as const,
  workflow: (id: string) => ['workflow', id] as const,
  workflowRuns: (id: string, segment?: string) => ['workflowRuns', id, segment ?? 'all'] as const,
  workflowRun: (runId: string) => ['workflowRun', runId] as const,
  workflowFires: (id: string) => ['workflowFires', id] as const,
  workflowSettings: () => ['workflowSettings'] as const,

  /**
   * CSV IMPORT AND EXPORT (csv-import-export.md D29, D34).
   *
   * `importRun` is its own entry and is POLLED at 2 seconds while the run is `queued` or
   * `running` - the doc's own number, faster than the metadata poll because a human is
   * watching and the read is one indexed row. The list is separate and is NOT polled: a
   * history screen that re-read fifty rows every two seconds would be paying for the one
   * row somebody is looking at.
   *
   * `importWizard` is a SECOND entry for the same run rather than a field on the first,
   * because they have different lifetimes: the run row changes on every counter update
   * and the wizard read (headers, samples, the target list) changes only when the file or
   * the data model does. One key would re-fetch the samples of a 500-row scan twice a
   * second while a run is going.
   *
   * `importColumnValues` is keyed by the COLUMN, because that is the grain of both the
   * read and the fix route (D43): one column's edits fail or land on their own.
   */
  imports: () => ['imports'] as const,
  importRun: (runId: string) => ['imports', runId] as const,
  importWizard: (runId: string) => ['imports', runId, 'wizard'] as const,
  importColumnValues: (runId: string, columnIndex: number) =>
    ['imports', runId, 'columns', columnIndex] as const,
  /** D44's three numbers, read fresh every time the confirm dialog opens. */
  importRevertPreview: (runId: string) => ['imports', runId, 'revertPreview'] as const,
  exports: () => ['exports'] as const,

  /**
   * INBOUND INTAKE (`inbound-intake.md` D19). One entry for the list, one per source, and
   * the COUNTS under their own key beside the source rather than inside it.
   *
   * The split is the same one the workflow list and its runs make, for the same reason: a
   * source's definition changes when somebody edits it and never otherwise, while its
   * counts move every time an external system delivers something - which is the whole point
   * of a door. One entry for both would either re-read the definition every few seconds or
   * leave the counts stale, and the counts are the half a person opens this screen for.
   *
   * THE MINTED KEY HAS NO ENTRY HERE, deliberately: it is a one-time response and a cache
   * is a place values are read back from. `useMintIntakeKey` is a mutation whose result the
   * screen holds in component state until navigation unmounts it (D19's one-time reveal).
   */
  intakeSources: () => ['intakeSources'] as const,
  intakeSource: (sourceId: string) => ['intakeSource', sourceId] as const,
  intakeSourceStatus: (sourceId: string) => ['intakeSource', sourceId, 'status'] as const,

  /**
   * THE HEALTH RULES (`phase-b-coach.md` D12), ONE ENTRY FOR ALL OF THEM.
   *
   * There is one rule set per object and, today, one object - so a key per config would be
   * a map with one entry and two ways to invalidate it. The RECORD PAGE reads this same
   * entry to find its computed block, which is the point: the Settings page editing a rule
   * and the record page rendering the block it describes must not hold two answers.
   */
  healthConfigs: () => ['healthConfigs'] as const,
  /**
   * THE DATA AGES (redesign D9): one entry for every page, so the Dashboard's line, the
   * Onboarding title's age and the stale notice read the same answer, and "Retry now"
   * refreshes all of them with one invalidation.
   */
  feedsStatus: () => ['feedsStatus'] as const,
  /** The colour history (redesign D10), per `since` day and owner (`all` or a team member). */
  healthHistory: (since: string, owner: string) => ['healthHistory', since, owner] as const,
} as const;

/**
 * PREFIXES, for invalidation only (INV-13 keeps them in this file with the keys).
 *
 * A metadata mutation has to reach BOTH visibilities: the admin list and the record
 * screens hold two cache entries of the same rows, and refreshing one while leaving the
 * other is how an archived field stays on a record page. TanStack matches by prefix, so
 * these are the keys minus their last segment - never a query key in their own right.
 */
export const queryKeyPrefixes = {
  objects: () => ['objects'] as const,
  object: (objectId: string) => ['object', objectId] as const,
  fields: (objectId: string) => ['fields', objectId] as const,
} as const;
